import { PlusOutlined, SearchOutlined } from "@ant-design/icons";
import { useCallback, useEffect, useMemo, useState } from "react";
import {
  Button, Card, Col, DatePicker, Form, Input, InputNumber,
  Row, Space, Tabs, Table, Tag, Typography, message
} from "antd";
import { StaticDrawer } from "../../shared/components/StaticDrawer";
import { StaticModal } from "../../shared/components/StaticModal";
import dayjs from "dayjs";
import { useNavigate } from "react-router-dom";
import { transportApi } from "../../api/transportApi";
import { getDefaultContext } from "../../api/httpClient";
import type { TripDto } from "../../types/api";
import { getEnteredByUser } from "../../utils/currentUser";
import { getApiErrorMessage } from "../../utils/apiError";
import { LookupSelect } from "../../shared/components/LookupSelect";
import { LocationAutoComplete } from "../../shared/components/LocationAutoComplete";
import { SaveActionButton } from "../../shared/components/SaveActionButton";
import { VehicleCreateModal } from "../../shared/components/VehicleCreateModal";
import { DriverCreateModal } from "../../shared/components/DriverCreateModal";
import type { LookupItem } from "../../shared/components/LookupModal";

export const TripListPage = () => {
  const navigate = useNavigate();
  const ctx = useMemo(() => getDefaultContext(), []);

  // ── list state ────────────────────────────────────────────────────────────
  const [rows, setRows] = useState<TripDto[]>([]);
  const [loading, setLoading] = useState(false);
  const [search, setSearch] = useState("");
  const [page, setPage] = useState(1);
  const [pageSize, setPageSize] = useState(25);
  const [vehicleMap, setVehicleMap] = useState<Record<number, string>>({});
  const [driverMap, setDriverMap] = useState<Record<number, string>>({});

  // ── create trip drawer ───────────────────────────────────────────────────
  const [open, setOpen] = useState(false);
  const [saving, setSaving] = useState(false);
  const [form] = Form.useForm();

  // ── complete trip drawer ─────────────────────────────────────────────────
  const [completeOpen, setCompleteOpen] = useState(false);
  const [completing, setCompleting] = useState(false);
  const [selectedTrip, setSelectedTrip] = useState<TripDto | null>(null);
  const [completeForm] = Form.useForm();

  // ── trip detail drawer ────────────────────────────────────────────────────
  const [detailOpen, setDetailOpen] = useState(false);
  const [detailTrip, setDetailTrip] = useState<TripDto | null>(null);
  const [detailTab, setDetailTab] = useState("fuel");
  const [detailLoading, setDetailLoading] = useState(false);
  const [fuelRows, setFuelRows] = useState<any[]>([]);
  const [expenseRows, setExpenseRows] = useState<any[]>([]);
  const [maintenanceRows, setMaintenanceRows] = useState<any[]>([]);

  // ── add modals inside detail drawer ──────────────────────────────────────
  const [fuelOpen, setFuelOpen] = useState(false);
  const [expenseOpen, setExpenseOpen] = useState(false);
  const [maintenanceOpen, setMaintenanceOpen] = useState(false);
  const [modalSaving, setModalSaving] = useState<string | null>(null);
  const [fuelForm] = Form.useForm();
  const [expenseForm] = Form.useForm();
  const [maintenanceForm] = Form.useForm();

  // ── lookups ───────────────────────────────────────────────────────────────
  const [vehicles, setVehicles] = useState<Array<{ value: number; label: string }>>([]);

  const vehicleFetcher = useCallback(
    (q: string, _page: number, pageSize: number) => transportApi.searchVehicleLookups(q, pageSize),
    []
  );
  const driverFetcher = useCallback(
    (q: string, _page: number, pageSize: number) => transportApi.searchDriverLookups(q, pageSize),
    []
  );

  // Inline create modals
  const [vehicleCreateOpen, setVehicleCreateOpen] = useState(false);
  const [vehiclePrefill, setVehiclePrefill]       = useState("");
  const [vehicleSeeds, setVehicleSeeds]           = useState<LookupItem[]>([]);
  const [driverCreateOpen, setDriverCreateOpen]   = useState(false);
  const [driverPrefill, setDriverPrefill]         = useState("");
  const [driverSeeds, setDriverSeeds]             = useState<LookupItem[]>([]);

  // ── data loading ──────────────────────────────────────────────────────────
  const load = async () => {
    setLoading(true);
    try {
      const [tripResult, vehicleResult, driverResult] = await Promise.all([
        transportApi.listTrips({ page: 1, pageSize: 500, q: search.trim() || undefined }),
        transportApi.searchVehicleLookups("", 500),
        transportApi.searchDriverLookups("", 500),
      ]);
      const nextVehicleMap = Object.fromEntries((vehicleResult.items ?? []).map((item) => [item.id, item.label]));
      const nextDriverMap = Object.fromEntries((driverResult.items ?? []).map((item) => [item.id, item.label]));
      setVehicleMap(nextVehicleMap);
      setDriverMap(nextDriverMap);
      setVehicles((vehicleResult.items ?? []).map((x) => ({ value: x.id, label: x.label })));
      setPage(1);
      setRows(tripResult.data.map((row) => ({
        ...row,
        vehicleName: nextVehicleMap[row.vehicleId] ?? `Vehicle #${row.vehicleId}`,
        driverName: nextDriverMap[row.driverId] ?? `Driver #${row.driverId}`,
      })));
    } catch (error) {
      message.error(getApiErrorMessage(error, "Unable to load trips"));
    } finally {
      setLoading(false);
    }
  };

  useEffect(() => { void load(); }, []);

  const loadDetailData = async (trip: TripDto) => {
    setDetailLoading(true);
    try {
      const [fuel, expenses, maint] = await Promise.all([
        transportApi.listFuelLogs({ pageSize: 200, tripId: trip.id }),
        transportApi.listTripExpenses({ pageSize: 200, tripId: trip.id }),
        transportApi.listMaintenances({ pageSize: 200, vehicleId: trip.vehicleId }),
      ]);
      setFuelRows(fuel.data);
      setExpenseRows(expenses.data);
      setMaintenanceRows(maint.data);
    } catch (error) {
      message.error(getApiErrorMessage(error, "Unable to load trip details"));
    } finally {
      setDetailLoading(false);
    }
  };

  const openDetail = (trip: TripDto) => {
    setDetailTrip(trip);
    setDetailTab("fuel");
    setDetailOpen(true);
    void loadDetailData(trip);
  };

  // ── filtered list ─────────────────────────────────────────────────────────
  const filteredRows = useMemo(() => {
    const term = search.trim().toLowerCase();
    if (!term) return rows;
    return rows.filter((row: any) =>
      String(row.id).includes(term) ||
      (row.vehicleName ?? vehicleMap[row.vehicleId] ?? "").toLowerCase().includes(term) ||
      (row.driverName ?? driverMap[row.driverId] ?? "").toLowerCase().includes(term) ||
      row.origin.toLowerCase().includes(term) ||
      row.destination.toLowerCase().includes(term) ||
      (row.endDate ? "completed" : "active").includes(term)
    );
  }, [rows, search, vehicleMap, driverMap]);

  // ── create trip ───────────────────────────────────────────────────────────
  const onVehicleSelected = async (vehicleId: number | undefined) => {
    if (!vehicleId) return;
    try {
      const r = await transportApi.getVehicleLastOdometer(vehicleId);
      if (r.lastOdometer != null) {
        form.setFieldValue("startOdometer", r.lastOdometer);
        message.info(`Last odometer auto-filled: ${r.lastOdometer} km`);
      }
    } catch { /* silent */ }
  };

  const onCreate = async () => {
    const values = await form.validateFields();
    setSaving(true);
    try {
      await transportApi.createTrip({
        ...ctx,
        enteredBy: getEnteredByUser(),
        vehicleId: values.vehicleId,
        driverId: values.driverId,
        origin: values.origin,
        destination: values.destination,
        startDate: values.startDate.toISOString(),
        startOdometer: values.startOdometer,
        remarks: values.remarks,
      });
      message.success("Trip started");
      setOpen(false);
      form.resetFields();
      await load();
    } catch (error) {
      message.error(getApiErrorMessage(error, "Unable to create trip"));
    } finally {
      setSaving(false);
    }
  };

  // ── complete trip ─────────────────────────────────────────────────────────
  const onOpenComplete = (row: TripDto) => {
    setSelectedTrip(row);
    completeForm.setFieldsValue({ endDate: dayjs(), endOdometer: row.endOdometer ?? row.startOdometer });
    setCompleteOpen(true);
  };

  const onComplete = async () => {
    if (!selectedTrip) return;
    const values = await completeForm.validateFields();
    setCompleting(true);
    try {
      await transportApi.completeTrip(selectedTrip.id, {
        organizationId: selectedTrip.organizationId,
        financialYearId: selectedTrip.financialYearId,
        endDate: values.endDate.toISOString(),
        endOdometer: values.endOdometer,
      });
      message.success("Trip completed");
      setCompleteOpen(false);
      await load();
    } catch (error) {
      message.error(getApiErrorMessage(error, "Unable to complete trip"));
    } finally {
      setCompleting(false);
    }
  };

  // ── log helpers ───────────────────────────────────────────────────────────
  const submitLog = async (key: string, work: () => Promise<void>, successMsg: string, close: () => void, reset: () => void) => {
    setModalSaving(key);
    try {
      await work();
      message.success(successMsg);
      close();
      reset();
      if (detailTrip) await loadDetailData(detailTrip);
    } catch (e) {
      message.error(getApiErrorMessage(e, "Unable to save"));
    } finally {
      setModalSaving(null);
    }
  };

  const onAddFuel = (values: any) =>
    submitLog("fuel", () => transportApi.addFuelLog({
      ...ctx, enteredBy: getEnteredByUser(),
      vehicleId: detailTrip!.vehicleId,
      tripId: detailTrip!.id,
      quantity: values.quantity,
      cost: values.cost,
      odometer: values.odometer,
      fuelDate: values.fuelDate.toISOString(),
      stationName: values.stationName,
    }), "Fuel log added", () => setFuelOpen(false), () => fuelForm.resetFields());

  const onAddExpense = (values: any) =>
    submitLog("expense", () => transportApi.addTripExpense({
      ...ctx, enteredBy: getEnteredByUser(),
      tripId: detailTrip!.id,
      expenseType: values.expenseType,
      amount: values.amount,
      tripExpenseRemarks: values.tripExpenseRemarks,
    }), "Expense recorded", () => setExpenseOpen(false), () => expenseForm.resetFields());

  const onAddMaintenance = (values: any) =>
    submitLog("maintenance", () => transportApi.addMaintenance({
      ...ctx, enteredBy: getEnteredByUser(),
      vehicleId: detailTrip!.vehicleId,
      serviceType: values.serviceType,
      vendor: values.vendor,
      cost: values.cost,
      serviceDate: values.serviceDate.toISOString(),
      odometer: values.odometer,
      nextServiceDue: values.nextServiceDue?.toISOString() ?? null,
    }), "Maintenance saved", () => setMaintenanceOpen(false), () => maintenanceForm.resetFields());

  // ── trip detail tabs ──────────────────────────────────────────────────────
  const detailVehicleName = detailTrip ? (vehicleMap[detailTrip.vehicleId] ?? `Vehicle #${detailTrip.vehicleId}`) : "";
  const detailDriverName = detailTrip ? (driverMap[detailTrip.driverId] ?? `Driver #${detailTrip.driverId}`) : "";

  const detailTabItems = [
    {
      key: "fuel",
      label: `Fuel Logs (${fuelRows.length})`,
      children: (
        <div>
          <div style={{ display: "flex", justifyContent: "flex-end", marginBottom: 8 }}>
            <Button
              type="primary" size="small" icon={<PlusOutlined />}
              onClick={() => { fuelForm.setFieldsValue({ fuelDate: dayjs() }); setFuelOpen(true); }}
            >Add Fuel Log</Button>
          </div>
          <Table
            rowKey="id" size="small" loading={detailLoading}
            dataSource={fuelRows} pagination={{ pageSize: 10 }}
            columns={[
              { title: "Date", dataIndex: "fuelDate", width: 100, render: (v: string) => dayjs(v).format("DD/MM/YY") },
              { title: "Station", dataIndex: "stationName" },
              { title: "Qty (L)", dataIndex: "quantity", width: 80 },
              { title: "Cost", dataIndex: "cost", width: 80 },
              { title: "Odometer", dataIndex: "odometer", width: 90 },
            ]}
          />
        </div>
      ),
    },
    {
      key: "expenses",
      label: `Expenses (${expenseRows.length})`,
      children: (
        <div>
          <div style={{ display: "flex", justifyContent: "flex-end", marginBottom: 8 }}>
            <Button
              type="primary" size="small" icon={<PlusOutlined />}
              onClick={() => setExpenseOpen(true)}
            >Add Expense</Button>
          </div>
          <Table
            rowKey="id" size="small" loading={detailLoading}
            dataSource={expenseRows} pagination={{ pageSize: 10 }}
            columns={[
              { title: "Type", dataIndex: "expenseType" },
              { title: "Amount", dataIndex: "amount", width: 90 },
              { title: "Remarks", dataIndex: "tripExpenseRemarks" },
            ]}
          />
        </div>
      ),
    },
    {
      key: "maintenance",
      label: `Maintenance (${maintenanceRows.length})`,
      children: (
        <div>
          <div style={{ display: "flex", justifyContent: "flex-end", marginBottom: 8 }}>
            <Button
              type="primary" size="small" icon={<PlusOutlined />}
              onClick={() => { maintenanceForm.setFieldsValue({ serviceDate: dayjs() }); setMaintenanceOpen(true); }}
            >Add Maintenance</Button>
          </div>
          <Table
            rowKey="id" size="small" loading={detailLoading}
            dataSource={maintenanceRows} pagination={{ pageSize: 10 }}
            columns={[
              { title: "Date", dataIndex: "serviceDate", width: 100, render: (v: string) => dayjs(v).format("DD/MM/YY") },
              { title: "Service Type", dataIndex: "serviceType" },
              { title: "Vendor", dataIndex: "vendor" },
              { title: "Cost", dataIndex: "cost", width: 80 },
              { title: "Odometer", dataIndex: "odometer", width: 90 },
            ]}
          />
        </div>
      ),
    },
  ];

  // ─────────────────────────────────────────────────────────────────────────
  return (
    <Card className="panel-card">
      <div className="panel-title-row">
        <Typography.Title level={3}>Trips</Typography.Title>
        <Button type="primary" onClick={() => { form.resetFields(); form.setFieldsValue({ startDate: dayjs() }); setOpen(true); }}>
          Start Trip
        </Button>
      </div>

      <Space style={{ width: "100%", justifyContent: "space-between", marginBottom: 16 }} wrap>
        <Input
          prefix={<SearchOutlined />}
          placeholder="Search trip, vehicle, driver, route..."
          allowClear
          value={search}
          onChange={(e) => setSearch(e.target.value)}
          style={{ width: 340 }}
        />
        <Typography.Text type="secondary">
          Showing {filteredRows.length} of {rows.length} loaded trips
        </Typography.Text>
      </Space>

      <Table
        rowKey="id"
        loading={loading}
        dataSource={filteredRows}
        size="small"
        pagination={{
          current: page,
          pageSize,
          total: filteredRows.length,
          showSizeChanger: true,
          pageSizeOptions: ["25", "50", "100"],
          showTotal: (total, range) => `${range[0]}-${range[1]} of ${total} trips`,
          onChange: (nextPage, nextPageSize) => { setPage(nextPage); setPageSize(nextPageSize); }
        }}
        columns={[
          { title: "SN", width: 60, render: (_, __, index) => ((page - 1) * pageSize) + index + 1 },
          { title: "Trip ID", dataIndex: "id", width: 75 },
          { title: "Vehicle", width: 140, render: (_, row: any) => row.vehicleName ?? vehicleMap[row.vehicleId] ?? `Vehicle #${row.vehicleId}` },
          { title: "Driver", width: 140, render: (_, row: any) => row.driverName ?? driverMap[row.driverId] ?? `Driver #${row.driverId}` },
          { title: "Origin", dataIndex: "origin" },
          { title: "Destination", dataIndex: "destination" },
          { title: "Start", dataIndex: "startDate", width: 105, render: (v: string) => dayjs(v).format("DD MMM YY") },
          {
            title: "Status", width: 95,
            render: (_, row) => (
              <Tag color={row.endDate ? "default" : "green"}>{row.endDate ? "Completed" : "Active"}</Tag>
            ),
          },
          {
            title: "", width: 170,
            render: (_, row) => (
              <Space size={4}>
                <Button size="small" onClick={() => openDetail(row)}>Details</Button>
                {!row.endDate && (
                  <Button size="small" onClick={() => onOpenComplete(row)}>Complete</Button>
                )}
              </Space>
            ),
          },
        ]}
      />

      {/* ── Start Trip Drawer ── */}
      <StaticDrawer
        title="Start New Trip"
        open={open}
        onClose={() => setOpen(false)}
        width={640}
        footer={<div style={{ display: "flex", justifyContent: "flex-end", gap: 8 }}><Button onClick={() => setOpen(false)}>Cancel</Button><SaveActionButton saving={saving} idleText="Save" savingText="Starting Trip..." onClick={() => void onCreate()} /></div>}
        maskClosable={false}
        keyboard={false}
      >
        <Form
          form={form}
          layout="vertical"
          initialValues={{ startDate: dayjs() }}
          onValuesChange={async (changed) => {
            if (changed.vehicleId) await onVehicleSelected(changed.vehicleId);
          }}
        >
          <Form.Item name="vehicleId" label="Vehicle" rules={[{ required: true, message: "Select a vehicle" }]}>
            <LookupSelect fetcher={vehicleFetcher} placeholder="Search by vehicle number..."
              addNewLabel="Vehicle" seededOptions={vehicleSeeds}
              onAddNew={(q) => { setVehiclePrefill(q); setVehicleCreateOpen(true); }} />
          </Form.Item>
          <Form.Item name="driverId" label="Driver" rules={[{ required: true, message: "Select a driver" }]}>
            <LookupSelect fetcher={driverFetcher} placeholder="Search by name or license..."
              addNewLabel="Driver" seededOptions={driverSeeds}
              onAddNew={(q) => { setDriverPrefill(q); setDriverCreateOpen(true); }} />
          </Form.Item>
          <Form.Item name="startDate" label="Start Date" rules={[{ required: true }]}>
            <DatePicker showTime style={{ width: "100%" }} />
          </Form.Item>
          <Form.Item name="startOdometer" label="Start Odometer (km)" rules={[{ required: true }]} extra="Auto-filled from last trip when vehicle is selected">
            <InputNumber style={{ width: "100%" }} min={0} />
          </Form.Item>
          <Form.Item name="origin" label="Origin" rules={[{ required: true }]}>
            <LocationAutoComplete placeholder="Search or enter departure city..." fieldLabel="Origin" />
          </Form.Item>
          <Form.Item name="destination" label="Destination" rules={[{ required: true }]}>
            <LocationAutoComplete placeholder="Search or enter arrival city..." fieldLabel="Destination" />
          </Form.Item>
          <Form.Item name="remarks" label="Remarks">
            <Input.TextArea rows={3} />
          </Form.Item>
        </Form>
      </StaticDrawer>

      {/* ── Complete Trip Drawer ── */}
      <StaticDrawer
        title="Complete Trip"
        open={completeOpen}
        onClose={() => setCompleteOpen(false)}
        width={480}
        footer={<div style={{ display: "flex", justifyContent: "flex-end", gap: 8 }}><Button onClick={() => setCompleteOpen(false)}>Cancel</Button><SaveActionButton saving={completing} idleText="Mark Complete" savingText="Completing Trip..." onClick={() => void onComplete()} /></div>}
        maskClosable={false}
        keyboard={false}
      >
        <Form form={completeForm} layout="vertical">
          <Form.Item name="endDate" label="End Date" rules={[{ required: true }]}>
            <DatePicker showTime style={{ width: "100%" }} />
          </Form.Item>
          <Form.Item name="endOdometer" label="End Odometer (km)" rules={[{ required: true }]}>
            <InputNumber style={{ width: "100%" }} min={0} />
          </Form.Item>
        </Form>
      </StaticDrawer>

      {/* ── Trip Detail Drawer ── */}
      <StaticDrawer
        title={
          detailTrip
            ? `Trip #${detailTrip.id} — ${detailVehicleName} · ${detailDriverName}`
            : "Trip Details"
        }
        open={detailOpen}
        onClose={() => setDetailOpen(false)}
        width={760}
        footer={
          <div style={{ display: "flex", justifyContent: "flex-end" }}>
            <Button onClick={() => setDetailOpen(false)}>Close</Button>
          </div>
        }
      >
        {detailTrip && (
          <>
            <Row gutter={16} style={{ marginBottom: 16 }}>
              <Col span={8}>
                <Typography.Text type="secondary">Route</Typography.Text>
                <div>{detailTrip.origin} → {detailTrip.destination}</div>
              </Col>
              <Col span={8}>
                <Typography.Text type="secondary">Start Date</Typography.Text>
                <div>{dayjs(detailTrip.startDate).format("DD MMM YYYY")}</div>
              </Col>
              <Col span={8}>
                <Typography.Text type="secondary">Status</Typography.Text>
                <div>
                  <Tag color={detailTrip.endDate ? "default" : "green"}>
                    {detailTrip.endDate ? `Completed ${dayjs(detailTrip.endDate).format("DD MMM YY")}` : "Active"}
                  </Tag>
                </div>
              </Col>
            </Row>

            <Tabs
              activeKey={detailTab}
              onChange={setDetailTab}
              items={detailTabItems}
              size="small"
            />
          </>
        )}
      </StaticDrawer>

      {/* ── Add Fuel Log Modal ── */}
      <StaticModal
        title="Add Fuel Log"
        open={fuelOpen}
        onCancel={() => setFuelOpen(false)}
        onOk={() => fuelForm.submit()}
        confirmLoading={modalSaving === "fuel"}
        width={640}
        destroyOnHidden
      >
        <Form form={fuelForm} layout="vertical" initialValues={{ fuelDate: dayjs() }} onFinish={(v) => void onAddFuel(v)}>
          <Row gutter={12}>
            <Col span={8}><Form.Item name="quantity" label="Quantity (L)" rules={[{ required: true }]}><InputNumber style={{ width: "100%" }} min={0} /></Form.Item></Col>
            <Col span={8}><Form.Item name="cost" label="Cost (₹)" rules={[{ required: true }]}><InputNumber style={{ width: "100%" }} min={0} /></Form.Item></Col>
            <Col span={8}><Form.Item name="odometer" label="Odometer (km)" rules={[{ required: true }]}><InputNumber style={{ width: "100%" }} min={0} /></Form.Item></Col>
          </Row>
          <Row gutter={12}>
            <Col span={12}><Form.Item name="fuelDate" label="Date" rules={[{ required: true }]}><DatePicker showTime style={{ width: "100%" }} /></Form.Item></Col>
            <Col span={12}><Form.Item name="stationName" label="Station Name" rules={[{ required: true }]}><Input /></Form.Item></Col>
          </Row>
        </Form>
      </StaticModal>

      {/* ── Add Trip Expense Modal ── */}
      <StaticModal
        title="Add Trip Expense"
        open={expenseOpen}
        onCancel={() => setExpenseOpen(false)}
        onOk={() => expenseForm.submit()}
        confirmLoading={modalSaving === "expense"}
        width={560}
        destroyOnHidden
      >
        <Form form={expenseForm} layout="vertical" onFinish={(v) => void onAddExpense(v)}>
          <Row gutter={12}>
            <Col span={12}><Form.Item name="expenseType" label="Expense Type" rules={[{ required: true }]}><Input placeholder="e.g. Toll, Driver Allowance" /></Form.Item></Col>
            <Col span={12}><Form.Item name="amount" label="Amount (₹)" rules={[{ required: true }]}><InputNumber style={{ width: "100%" }} min={0} /></Form.Item></Col>
          </Row>
          <Form.Item name="tripExpenseRemarks" label="Remarks"><Input.TextArea rows={2} /></Form.Item>
        </Form>
      </StaticModal>

      {/* ── Add Maintenance Modal ── */}
      <StaticModal
        title="Add Maintenance"
        open={maintenanceOpen}
        onCancel={() => setMaintenanceOpen(false)}
        onOk={() => maintenanceForm.submit()}
        confirmLoading={modalSaving === "maintenance"}
        width={640}
        destroyOnHidden
      >
        <Form form={maintenanceForm} layout="vertical" initialValues={{ serviceDate: dayjs() }} onFinish={(v) => void onAddMaintenance(v)}>
          <Row gutter={12}>
            <Col span={12}><Form.Item name="serviceType" label="Service Type" rules={[{ required: true }]}><Input /></Form.Item></Col>
            <Col span={12}><Form.Item name="vendor" label="Vendor / Workshop" rules={[{ required: true }]}><Input /></Form.Item></Col>
          </Row>
          <Row gutter={12}>
            <Col span={8}><Form.Item name="cost" label="Cost (₹)" rules={[{ required: true }]}><InputNumber style={{ width: "100%" }} min={0} /></Form.Item></Col>
            <Col span={8}><Form.Item name="odometer" label="Odometer (km)" rules={[{ required: true }]}><InputNumber style={{ width: "100%" }} min={0} /></Form.Item></Col>
            <Col span={8}><Form.Item name="serviceDate" label="Service Date" rules={[{ required: true }]}><DatePicker style={{ width: "100%" }} /></Form.Item></Col>
          </Row>
          <Form.Item name="nextServiceDue" label="Next Service Due"><DatePicker style={{ width: "100%" }} /></Form.Item>
        </Form>
      </StaticModal>

      <VehicleCreateModal
        open={vehicleCreateOpen}
        prefillName={vehiclePrefill}
        onClose={() => setVehicleCreateOpen(false)}
        onCreated={(item) => {
          setVehicleSeeds(prev => [item, ...prev.filter(x => x.id !== item.id)]);
          form.setFieldValue("vehicleId", item.id);
          setVehicleCreateOpen(false);
        }}
      />
      <DriverCreateModal
        open={driverCreateOpen}
        prefillName={driverPrefill}
        onClose={() => setDriverCreateOpen(false)}
        onCreated={(item) => {
          setDriverSeeds(prev => [item, ...prev.filter(x => x.id !== item.id)]);
          form.setFieldValue("driverId", item.id);
          setDriverCreateOpen(false);
        }}
      />
    </Card>
  );
};
